import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { certImageUrl, postToLinkedIn } from "@/components/Certificate";
import { Sparkles, Loader2, ArrowRight, BadgeCheck, Linkedin } from "lucide-react";

export default function CertificatePage() {
  const { id } = useParams();
  const [cert, setCert] = useState(undefined);
  useEffect(() => { api.get(`/cert/${id}`).then((r) => setCert(r.data)).catch(() => setCert(null)); }, [id]);

  if (cert === undefined) return <div className="min-h-screen flex items-center justify-center text-emerald-600" style={{ background: "#F4F7FB" }}><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen grid-bg text-slate-900" style={{ background: "#F4F7FB" }} data-testid="cert-page">
      <header className="max-w-4xl mx-auto px-6 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="cert-home-link">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center glow-emerald"><Sparkles className="w-5 h-5 text-black" /></div>
          <span className="font-display font-extrabold">Pitch Craft</span>
        </Link>
        <Link to="/register"><Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid="cert-cta-top">Try it free</Button></Link>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        {!cert ? (
          <div className="card-surface rounded-3xl p-10 text-center" data-testid="cert-missing">
            <h1 className="text-2xl font-bold">This certificate isn't available</h1>
            <p className="text-slate-500 mt-2">The link may be wrong or the certificate has been withdrawn.</p>
          </div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center">
              <p className="inline-flex items-center gap-1.5 text-emerald-700 font-mono text-xs uppercase tracking-widest bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1" data-testid="cert-verified"><BadgeCheck className="w-4 h-4" /> Verified certificate</p>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-3" data-testid="cert-headline">{cert.rep_name} completed <span className="text-emerald-600">{cert.course_title}</span></h1>
              <p className="text-slate-500 mt-2">Level {cert.level} · {cert.lesson_count} lessons · issued {new Date(cert.issued_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · ID {cert.id}</p>
              <Button onClick={() => postToLinkedIn(cert)} size="sm" className="mt-4 bg-[#0A66C2] hover:bg-[#004182] text-white font-semibold rounded-full" data-testid="cert-linkedin-btn"><Linkedin className="w-4 h-4 mr-1.5" /> Post to LinkedIn</Button>
            </motion.div>
            <motion.img initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }} src={certImageUrl(cert.id)} alt={`Certificate for ${cert.rep_name}`} className="w-full rounded-2xl border border-slate-200 shadow-xl" data-testid="cert-image" />
            <div className="card-surface rounded-3xl p-8 text-center">
              <h2 className="text-base md:text-lg font-bold">Want your own?</h2>
              <p className="text-slate-500 mt-1">Pitch Craft is the AI sales coach: practise real conversations with AI customers, get scored, and earn certificates as you level up.</p>
              <Link to="/register"><Button className="mt-4 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold h-11 px-6" data-testid="cert-cta">Start practising free <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
